import React, { useState, useEffect, FunctionComponent } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import * as dayjs from "dayjs"
import { useGetTraining } from "../../../common/api/Training/queries"
import { TrainingModel } from "../../../common/api/Training/TrainingApi"
import { useGetTrainingSessions } from "../../../common/api/TrainingSessions/queries"
import { CustomerUserTrainingSessionStates } from "@murphy-frontend/common/enums"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import logo from "../../../assets/img/Praeparatus_Supervivet_Practise_Risk.jpg"
import { parseHtml } from "@murphy-frontend/web-core/features/RichText/helpers"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useGetUserTrainingSessionsByCustomerUserId } from "../../../common/api/UserTrainingSessions/queries"
import { useUser } from "../../../common/contexts/UserContext"
import { useGetTrainingFiles } from "../../../common/api/TrainingFiles/queries"
import FileList from "@murphy-frontend/web-core/components/FileList"
import { getConvertedDate } from "@murphy-frontend/common/services/TimeService"
import FilterTag from "@murphy-frontend/web-core/components/FilterTag"
import Button from "@murphy-frontend/web-core/components/Button"
import { IconContext } from "react-icons/lib"
import { IoChevronBack } from "react-icons/io5"
import { Levels } from "@murphy-frontend/web-core/constants"
import { translateConstantsWithBackendTranslations } from "../../../common/services/translationService"
import CourseBooking from "../../../common/components/TrainingComponents/CourseCard/components/CourseBooking"
import { convertTrainingId } from "@murphy-frontend/common/utils"
import ComponentWrapper from "../../../common/components/ComponentWrapper"

interface TrainingView {
	id: number
	name: string
	imageUrl: string
	description: string
	htmlContent?: JSX.Element | JSX.Element[] | string
	trainingTypeId: number
	level?: number
	isMandatory: boolean
}

const CourseDescription: FunctionComponent = () => {
	const params = useParams()
	const id = parseInt(params.id)
	const [training, setTraining] = useState<TrainingModel>()
	const [trainingView, setTrainingView] = useState<TrainingView>()
	const [levels, setLevels] = useState(Levels)

	const [files, setFiles] = useState([])
	const { timeZone, translations, language } = useUserPreferences()

	const navigate = useNavigate()
	const { customer } = useCustomer()
	const { user } = useUser()

	useEffect(() => {
		if (translations) {
			const translatedLevels = translateConstantsWithBackendTranslations(Levels, translations)
			setLevels(translatedLevels)
		}
	}, [translations])

	const {
		isLoading: customerUserTrainingSessionsIsLoading,
		isError: customerUserTrainingSessionsIsError,
		data: customerUserTrainingSessionsData,
		error: customerUserTrainingSessionsError
	} = useGetUserTrainingSessionsByCustomerUserId(user?.CustomerUserId)

	const {
		isLoading: trainingsIsLoading,
		isError: trainingsIsError,
		data: trainingsData,
		error: trainingsError
	} = useGetTraining({ id, customerUserId: user?.CustomerUserId })

	const {
		isLoading: trainingFilesIsLoading,
		isError: trainingFilesIsError,
		data: trainingFilesData,
		error: trainingFilesError
	} = useGetTrainingFiles(id)

	const {
		isLoading: trainingSessionsIsLoading,
		isError: trainingSessionsIsError,
		data: trainingSessionsData,
		error: trainingSessionsError
	} = useGetTrainingSessions({ trainingId: id, customerId: customer?.Id, customerUserId: user?.CustomerUserId })

	const mapFiles = filesArray =>
		filesArray.map(file => ({
			id: file.ID,
			fileId: file.FileId,
			filepath: file.FilePath,
			filename: file.FileName,
			createddatetime: getConvertedDate(dayjs.utc(file.CreatedDateTime), timeZone)
		}))

	const createTrainingView = (trainingRaw: TrainingModel) => {
		const newTrainingView: TrainingView = {
			id: trainingRaw.Id,
			name: trainingRaw.Name,
			imageUrl: trainingRaw.ImageUrl,
			description: trainingRaw.Description,
			trainingTypeId: trainingRaw.TrainingTypeId,
			level: trainingRaw.Levels,
			isMandatory: trainingRaw.IsMandatory
		}

		if (trainingRaw.HtmlDescription) {
			newTrainingView.htmlContent = parseHtml(trainingRaw.HtmlDescription)

			// parse(trainingRaw.HtmlDescription, {
			//   replace: (node) => {
			//     if (
			//       node.parent &&
			//       node.parent.name &&
			//       node.parent.name === "a" &&
			//       node.parent.attribs &&
			//       node.parent.attribs.href
			//     ) {
			//       const { href } = node.parent.attribs;
			//       const youtubeRegex =
			//         /^(?:(?:https?:)?\/\/)?(?:(?:www|m)\.)?(?:(?:youtube\.com|youtu.be))(?:\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(?:\S+)?$/;
			//       const vimeoRegex =
			//         /(http|https)?:\/\/(www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|)(\d+)(?:|\/\?)/;
			//       const youtubeMatches = href.match(youtubeRegex);
			//       const vimeoMatches = href.match(vimeoRegex);
			//       if (vimeoMatches || youtubeMatches) {
			//         return <ResponsivePlayer url={href} />;
			//       }
			//     }
			//   },
			// });
		}
		return newTrainingView
	}

	const formatString = (str: string) => {
		const lines = str.split(/\n/)
		return lines.map((line, index) => (
			<React.Fragment key={index}>
				{line}
				<br />
			</React.Fragment>
		))
	}

	const onClickBack = () => {
		const url = `/courses`
		navigate(url)
	}

	useEffect(() => {
		if (!trainingFilesIsLoading && trainingFilesData) {
			const mappedFiles = mapFiles(trainingFilesData.data || [])
			setFiles(mappedFiles)
		}
	}, [trainingFilesIsLoading, trainingFilesData, timeZone])

	useEffect(() => {
		if (!trainingsIsLoading) {
			setTraining(trainingsData)
		}
		return () => {
			setTraining({})
		}
	}, [trainingsData, trainingsIsLoading])

	useEffect(() => {
		if (training) {
			const newTrainingView = createTrainingView(training)
			setTrainingView(newTrainingView)
		}
		return () => {
			setTrainingView({})
		}
	}, [training])

	if (!trainingView || trainingSessionsIsLoading || trainingsIsLoading || customerUserTrainingSessionsIsLoading) {
		return (
			<ComponentWrapper active={4}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	const bookedTrainingSessions = customerUserTrainingSessionsData?.filter(
		p => p.TrainingSessionStateId === CustomerUserTrainingSessionStates.Booked
	)

	let bookedTrainingSession = null
	if (bookedTrainingSessions?.length > 0) {
		bookedTrainingSession = bookedTrainingSessions[0]
	}

	return (
		<ComponentWrapper active={4}>
			<div
				className='course-description-wrapper'
				style={{
					backgroundImage: `url(${trainingView.imageUrl ?? logo})`
				}}
			>
				<div className='course-description-back'>
					<Button isLightButton={true} onClick={onClickBack}>
						<IconContext.Provider value={{ className: "dropdown-icon" }}>
							<IoChevronBack />
						</IconContext.Provider>
						<span>Back</span>
					</Button>
				</div>
				<div className='course-description-container'>
					{trainingView.isMandatory && (
						<FilterTag filterName={translations.mandatory} isMandatory={trainingView.isMandatory} />
					)}
					<h1 className='course-description-header'>{trainingView.name}</h1>
					{trainingView.trainingTypeId === 3 && (
						<div className='course-description-btn'>
							<Button>
								<span>Watch Recording</span>
							</Button>
						</div>
					)}
					<CourseBooking
						trainingSessions={trainingSessionsData}
						timeZone={timeZone}
						language={language}
						translations={translations}
						bookedTrainingSession={bookedTrainingSession}
						trainingTypeId={trainingView.trainingTypeId}
					/>
					<div className='flex-row-container row course-description-filters'>
						<FilterTag
							filterName={convertTrainingId(trainingView.trainingTypeId, translations)}
							passive={true}
						/>
						{trainingView?.level && (
							<FilterTag filterName={levels[trainingView.level].value} passive={true} />
						)}
						{/* <FilterTag filterName={"2 timmar"} inActive={true} /> */}
					</div>
					<div className='course-description-title'>{translations.summary}</div>
					<div className='course-description'>
						{trainingView.description && formatString(trainingView.description)}
					</div>
					<div className='course-description-title'>{translations["class-lang-modal-description"]}</div>
					<div className='course-description'>{trainingView.htmlContent && trainingView.htmlContent}</div>
					<div className='course-description-title'>{translations["class-lang-files"]}</div>
					<div className='course-description'>
						<FileList
							translations={translations}
							files={files}
							isReadOnly
							hidePageInputs={files.length < 10}
							hideFooter={files.length < 10}
						/>
					</div>
				</div>
			</div>
		</ComponentWrapper>
	)
}

export default CourseDescription